/* @jsx h */
import { defineComponent, h, computed, ref } from 'vue'
import type { NormalizedSchema, FieldOptions, Path, I18n, ValidationResult } from './types'
import { defaultI18n } from './i18n'
import { defaultTemplates, toggleTemplate } from './templates'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fieldLabel(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim()
}

function resolveLabel(
  name: string,
  schema: NormalizedSchema,
  opts: FieldOptions,
  auto: 'labels' | 'placeholders' | 'none',
): any {
  if (opts.label === false) return undefined
  if (opts.label != null) return typeof opts.label === 'function' ? (opts.label as () => any)() : opts.label
  if (auto === 'none' || auto === 'placeholders') return undefined
  const suffix = schema.optional ? defaultI18n.optional : defaultI18n.required
  return fieldLabel(name) + suffix
}

function resolvePlaceholder(
  name: string,
  schema: NormalizedSchema,
  opts: FieldOptions,
  auto: 'labels' | 'placeholders' | 'none',
): string | undefined {
  if (auto !== 'placeholders') return undefined
  const existing = opts.attrs?.placeholder
  if (existing) return existing
  const suffix = schema.optional ? defaultI18n.optional : defaultI18n.required
  return fieldLabel(name) + suffix
}

function resolveError(opts: FieldOptions, value: any, path: Path, context: any) {
  if (opts.error == null) return undefined
  if (typeof opts.error === 'function') return opts.error(value, path, context)
  return opts.error
}

function mergeI18n(partial?: Partial<I18n>): I18n {
  return { ...defaultI18n, ...partial }
}

function defaultValueForSchema(schema: NormalizedSchema): any {
  switch (schema.kind) {
    case 'string': return ''
    case 'number': return undefined
    case 'boolean': return false
    case 'object': {
      const obj: Record<string, any> = {}
      for (const [k, v] of Object.entries(schema.properties ?? {})) {
        if (!v.optional) obj[k] = defaultValueForSchema(v)
      }
      return obj
    }
    case 'array': return []
    case 'enum': return schema.enumValues?.[0]?.value ?? ''
    default: return undefined
  }
}

function sortEnumOptions(
  values: Array<{ value: string; label: string }>,
  order: 'asc' | 'desc' | string[] | undefined,
) {
  if (!order) return values
  if (Array.isArray(order)) {
    return [...values].sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value))
  }
  return [...values].sort((a, b) =>
    order === 'asc' ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label),
  )
}

// ---------------------------------------------------------------------------
// Props type for FieldRenderer
// ---------------------------------------------------------------------------

export interface FieldRendererProps {
  schema: NormalizedSchema
  value: any
  options: FieldOptions
  path: Path
  fieldName: string
  auto: 'labels' | 'placeholders' | 'none'
  disabled: boolean
  context?: any
  showErrors: boolean
  validationResult?: ValidationResult
  onChange: (value: any, path: Path) => void
}

// ---------------------------------------------------------------------------
// FieldRenderer
// ---------------------------------------------------------------------------

export const FieldRenderer = defineComponent<FieldRendererProps>({
  name: 'FieldRenderer',
  props: [
    'schema',
    'value',
    'options',
    'path',
    'fieldName',
    'auto',
    'disabled',
    'context',
    'showErrors',
    'validationResult',
    'onChange',
  ] as any,
  setup(props) {
    // If a custom factory component is provided, delegate entirely to it
    if (props.options?.factory) {
      const Factory = props.options.factory as any
      return () => (
        <Factory
          schema={props.schema}
          value={props.value}
          options={props.options}
          path={props.path}
          fieldName={props.fieldName}
          disabled={props.disabled}
          context={props.context}
          showErrors={props.showErrors}
          validationResult={props.validationResult}
          onChange={props.onChange}
        />
      )
    }

    return () => {
      const schema = props.schema
      const opts = props.options ?? {}
      const effectiveAuto = opts.auto ?? props.auto ?? 'labels'
      const effectiveDisabled = props.disabled || !!opts.disabled
      const label = resolveLabel(props.fieldName, schema, opts, effectiveAuto)
      const help = typeof opts.help === 'function' ? (opts.help as () => any)() : opts.help
      const error = resolveError(opts, props.value, props.path, props.context)
      const hasError = !!(opts.hasError || (props.showErrors && error != null))
      const config = opts.config ?? {}
      const context = props.context

      // ---- String/number with explicit options → Select ------------------
      if ((schema.kind === 'string' || schema.kind === 'number') && opts.options) {
        const nullOption =
          opts.nullOption === false
            ? false
            : opts.nullOption ?? (schema.optional ? { value: '', text: '---' } : false)

        const locals = {
          kind: 'select' as const,
          value: props.value ?? null,
          onChange: (v: string | null) => props.onChange(v, props.path),
          error,
          hasError,
          label,
          help,
          disabled: effectiveDisabled,
          attrs: opts.attrs ?? {},
          options: opts.options,
          nullOption,
          multiple: !!opts.multiple,
          path: props.path,
          config,
          context,
        }
        const template = opts.template ?? defaultTemplates.select
        return template(locals as any)
      }

      // ---- Textbox -------------------------------------------------------
      if (schema.kind === 'string' || schema.kind === 'number') {
        const transformer = opts.transformer
        const rawValue = props.value
        const displayValue = transformer
          ? transformer.format(rawValue)
          : rawValue != null
          ? String(rawValue)
          : ''

        const onChange = (display: string) => {
          const trimmed = display.trim() === '' ? null : display
          const parsed = transformer
            ? transformer.parse(display)
            : schema.kind === 'number'
            ? trimmed == null
              ? undefined
              : Number(trimmed)
            : trimmed
          props.onChange(parsed, props.path)
        }

        const placeholder = resolvePlaceholder(props.fieldName, schema, opts, effectiveAuto)
        const htmlType =
          opts.type ??
          (schema.format === 'email' ? 'email' : schema.format === 'uri' ? 'url' : 'text')

        const locals = {
          kind: 'textbox' as const,
          value: displayValue,
          onChange,
          error,
          hasError,
          label,
          help,
          disabled: effectiveDisabled,
          attrs: { ...(placeholder ? { placeholder } : {}), ...opts.attrs },
          type: htmlType,
          path: props.path,
          config,
          context,
        }

        const template = opts.template ?? defaultTemplates.textbox
        return template(locals as any)
      }

      // ---- Checkbox ------------------------------------------------------
      if (schema.kind === 'boolean') {
        const locals = {
          kind: 'checkbox' as const,
          value: !!props.value,
          onChange: (v: boolean) => props.onChange(v, props.path),
          error,
          hasError,
          label,
          help,
          disabled: effectiveDisabled,
          attrs: opts.attrs ?? {},
          path: props.path,
          config,
          context,
        }
        const template = opts.template ?? (opts.toggle ? toggleTemplate : defaultTemplates.checkbox)
        return template(locals as any)
      }

      // ---- Select --------------------------------------------------------
      if (schema.kind === 'enum') {
        const rawEnumValues = schema.enumValues ?? []
        const customOptions = opts.options
        const optionList = customOptions
          ? customOptions
          : sortEnumOptions(rawEnumValues, opts.order as any).map((ev) => ({
              value: ev.value,
              text: ev.label,
            }))

        const nullOption =
          opts.nullOption === false
            ? false
            : opts.nullOption ?? (schema.optional ? { value: '', text: '---' } : false)

        const locals = {
          kind: 'select' as const,
          value: props.value ?? null,
          onChange: (v: string | null) => props.onChange(v, props.path),
          error,
          hasError,
          label,
          help,
          disabled: effectiveDisabled,
          attrs: opts.attrs ?? {},
          options: optionList,
          nullOption,
          multiple: !!opts.multiple,
          path: props.path,
          config,
          context,
        }
        const template = opts.template ?? defaultTemplates.select
        return template(locals as any)
      }

      // ---- Object / Struct -----------------------------------------------
      if (schema.kind === 'object') {
        const properties = schema.properties ?? {}
        const fieldNames = Object.keys(properties)

        // Determine field order
        const order =
          opts.order ??
          fieldNames

        const structValue = props.value ?? {}

        const inputs: Record<string, () => ReturnType<typeof h>> = {}
        for (const key of fieldNames) {
          const fieldSchema = properties[key]
          const fieldOpts = opts.fields?.[key] ?? {}
          const fieldPath = [...props.path, key]
          inputs[key] = () => (
            <FieldRenderer
              schema={fieldSchema}
              value={structValue[key]}
              options={fieldOpts}
              path={fieldPath}
              fieldName={key}
              auto={effectiveAuto}
              disabled={effectiveDisabled}
              context={context}
              showErrors={props.showErrors}
              validationResult={props.validationResult}
              onChange={(v: any, p: Path) => {
                props.onChange({ ...structValue, [key]: v }, props.path)
              }}
            />
          )
        }

        const locals = {
          kind: 'struct' as const,
          value: structValue,
          onChange: (v: Record<string, any>) => props.onChange(v, props.path),
          error,
          hasError,
          label,
          legend: typeof opts.legend !== 'undefined' ? opts.legend : label,
          help,
          disabled: effectiveDisabled,
          inputs,
          order,
          path: props.path,
          config,
          context,
        }
        const template = opts.template ?? defaultTemplates.struct
        return template(locals as any)
      }

      // ---- Array / List --------------------------------------------------
      if (schema.kind === 'array') {
        const listValue: any[] = Array.isArray(props.value) ? props.value : []
        const itemSchema = schema.items
        const itemOpts = opts.item ?? {}
        const i18n = mergeI18n(opts.i18n)

        const items = listValue.map((itemValue, index) => ({
          key: index,
          input: () =>
            itemSchema ? (
              <FieldRenderer
                schema={itemSchema}
                value={itemValue}
                options={itemOpts}
                path={[...props.path, index]}
                fieldName={String(index)}
                auto={effectiveAuto}
                disabled={effectiveDisabled}
                context={context}
                showErrors={props.showErrors}
                validationResult={props.validationResult}
                onChange={(v: any) => {
                  const next = [...listValue]
                  next[index] = v
                  props.onChange(next, props.path)
                }}
              />
            ) : (
              <span>{String(itemValue)}</span>
            ),
        }))

        const locals = {
          kind: 'list' as const,
          value: listValue,
          onChange: (v: any[]) => props.onChange(v, props.path),
          error,
          hasError,
          label,
          legend: typeof opts.legend !== 'undefined' ? opts.legend : label,
          help,
          disabled: effectiveDisabled,
          items,
          onAdd: () => {
            const newItem = itemSchema ? defaultValueForSchema(itemSchema) : ''
            props.onChange([...listValue, newItem], props.path)
          },
          onRemove: (index: number) => {
            props.onChange(listValue.filter((_, i) => i !== index), props.path)
          },
          onMoveUp: (index: number) => {
            if (index === 0) return
            const next = [...listValue]
            ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
            props.onChange(next, props.path)
          },
          onMoveDown: (index: number) => {
            if (index >= listValue.length - 1) return
            const next = [...listValue]
            ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
            props.onChange(next, props.path)
          },
          disableAdd: !!opts.disableAdd || effectiveDisabled,
          disableRemove: !!opts.disableRemove || effectiveDisabled,
          disableOrder: !!opts.disableOrder,
          i18n,
          path: props.path,
          config,
          context,
        }
        const template = opts.template ?? defaultTemplates.list
        return template(locals as any)
      }

      // ---- Fallback ------------------------------------------------------
      return <span class="text-warning text-xs">Unsupported schema kind: {schema.kind}</span>
    }
  },
})
